import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { getTranslatedString } from 'helpers/utils-typescript';

const CURRENTLY_PLAYING_QUESTS = gql`
query currentlyPlayingQuests($currentUserId: UUID!) {
  activeSinglePlayerQuests: routes(
    filter: {
      routeStops: {
        some: {
          routeStopUserStatuses: {
            some: {
              userId: {equalTo: $currentUserId}
              hasCompleted: {equalTo: true}
              routeMultiplayerGameId: {isNull: true}
            }
          }
        }
      }
    }
  ) {
    nodes {
      id
      slug
      name
      routeStops {
        totalCount
      }
      completedRouteStops: routeStops(
        filter: {
          routeStopUserStatuses: {
            some: {
              userId: {equalTo: $currentUserId}
              hasCompleted: {equalTo: true}
              routeMultiplayerGameId: {isNull: true}
            }
          }
        }
      ) {
        totalCount
      }
      singlePlayerCompletedRouteStops: routeStops(
        filter: {
          routeStopUserStatuses: {
            some: {
              userId: {equalTo: $currentUserId}
              hasCompleted: {equalTo: true}
              routeMultiplayerGameId: {isNull: true}
            }
          }
        }
      ) {
        nodes {
          routeStopUserStatuses(
            filter: {
              userId: {equalTo: $currentUserId}
              hasCompleted: {equalTo: true}
              routeMultiplayerGameId: {isNull: true}
            }
            orderBy: UPDATED_AT_DESC
            first: 1
          ) {
            nodes {
              updatedAt
            }
          }
        }
      }
    }
  }
  activeMultiplayerGames: routeMultiplayerGames(
    filter: {
      status: {equalTo: IN_PROGRESS}
      players: {anyEqualTo: $currentUserId}
    }
  ) {
    nodes {
      id
      updatedAt
      route {
        id
        slug
        name
      }
    }
  }
}
`;

export type CurrentlyPlayingQuest = {
  id: string;
  routeId: string;
  name: string;
  url: string;
  mode: 'single-player' | 'multiplayer';
  lastActiveAt?: string;
};

const useCurrentlyPlayingQuests = (currentUserId?: string, currentLang = 'en') => {
  const { data, loading, error } = useQuery(CURRENTLY_PLAYING_QUESTS, {
    variables: {
      currentUserId,
    },
    skip: !currentUserId,
    fetchPolicy: 'network-only',
    pollInterval: 10000,
  });

  const quests = useMemo(() => {
    const multiplayerQuests = data?.activeMultiplayerGames?.nodes
      ?.filter((game: any) => game?.id && game?.route?.id && game?.route?.slug)
      ?.map((game: any) => {
        const route = game.route;

        return {
          id: game.id,
          routeId: route.id,
          name: getTranslatedString(route.name, currentLang) || route.slug,
          url: `/quest/${route.slug}/multiplayer/${game.id}`,
          mode: 'multiplayer' as const,
          lastActiveAt: game.updatedAt,
        };
      }) || [];

    const multiplayerQuestIds = new Set(multiplayerQuests.map((quest) => quest.routeId));

    const singlePlayerQuests = data?.activeSinglePlayerQuests?.nodes
      ?.filter((route: any) => {
        if (!route?.id || multiplayerQuestIds.has(route.id)) {
          return false;
        }

        const totalStops = route.routeStops?.totalCount || 0;
        const completedStops = route.completedRouteStops?.totalCount || 0;

        return completedStops > 0 && completedStops < totalStops;
      })
      ?.map((route: any) => {
        const lastActiveAt = route.singlePlayerCompletedRouteStops?.nodes
          ?.flatMap((routeStop: any) => routeStop?.routeStopUserStatuses?.nodes || [])
          ?.map((status: any) => status?.updatedAt)
          ?.filter(Boolean)
          ?.sort((left: string, right: string) => new Date(right).getTime() - new Date(left).getTime())?.[0];

        return {
          id: route.id,
          routeId: route.id,
          name: getTranslatedString(route.name, currentLang) || route.slug,
          url: `/quest/${route.slug}/map`,
          mode: 'single-player' as const,
          lastActiveAt,
        };
      }) || [];

    return [...multiplayerQuests, ...singlePlayerQuests] as CurrentlyPlayingQuest[];
  }, [currentLang, data]);

  return {
    error,
    hasPlayingQuests: quests.length > 0,
    loading,
    quests,
  };
};

export default useCurrentlyPlayingQuests;
